import React, { createContext, useState, useCallback, useContext, useEffect } from 'react';
import axios from 'axios';
import { isAuthenticated, logout } from '../services/authService'; // Assuming authService is correctly pathed
import { useTenant } from './TenantContext'; // Import useTenant
import { useUser } from './UserContext'; // Import useUser to check if user is authenticated
import { getEvents, getEventDetails } from '../services/businessService';

const EventContext = createContext();

export const useEvents = () => useContext(EventContext);

export const EventProvider = ({ children }) => {
  const [userEvents, setUserEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false); // Set to false initially
  const [errorEvents, setErrorEvents] = useState(null);
  const { selectedTenantId, loadingTenant, getTenantIdFromToken } = useTenant(); // Get tenant info, including loadingTenant
  const { userData, loadingUser } = useUser(); // Get user data and loading state


  const changeTenantIDCleanup = useCallback(() => {
    // Clear user events when tenant changes
    setUserEvents([]);
  }, []);

  // Basic logout handler, you might want to enhance this or move it to an AuthContext
  const handleLogout = useCallback(() => {
    logout();
    // Potentially navigate to login, depending on how you handle routing globally
    // For now, just clearing events
    setUserEvents(null); 
  }, []);

  const fetchUserEvent = useCallback(async (event_id) => {
    if (!isAuthenticated() || !userData || !selectedTenantId) return;

    // Try to fetch event details in userEvents
    const eventInUserEvents = userEvents.find(event => event.event_id === event_id);
    if (eventInUserEvents) return eventInUserEvents;

    try {
      return await getEventDetails(selectedTenantId, event_id);
    } catch (error) {
      console.error('Error fetching user event:', error);
    }
  }, [handleLogout, selectedTenantId, userData]);

  const fetchUserEventsV2 = useCallback(async () => {
    // Ensure user is authenticated and tenantId is available and neither user nor tenant info is loading.
    if (!isAuthenticated() || !userData) {
      setUserEvents([]);
      setLoadingEvents(false);
      return;
    }

    const currentTenantId = selectedTenantId; 
    if (!currentTenantId) {
      setUserEvents([]); 
      setLoadingEvents(false);
      return;
    }

    const cacheKey = `my_events_v2_${currentTenantId}_${userData.email}`;
    const cached = localStorage.getItem(cacheKey);

    // If we have cached data, display it immediately.
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setUserEvents(parsed);
        }
      } catch (e) { /* ignore parse error */ }
    } else {
      // Only set loading to true if there's no cached data.
      setLoadingEvents(true);
    }
    setErrorEvents(null);

    // Always fetch in the background to refresh data.
    try {
      // const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      // const response = await axios.get(`${BACKEND_URL}/app/t:${currentTenantId}/api/v2/events/`, {
      //   headers: {
      //     Authorization: `Bearer ${localStorage.getItem('access_token')}`
      //   }
      // });
      // if (response.data && Array.isArray(response.data.results)) {
      //   setUserEvents(response.data.results);
      //   localStorage.setItem(cacheKey, JSON.stringify(response.data.results));
      // }
      const events = await getEvents(currentTenantId);
      setUserEvents(events);
      localStorage.setItem(cacheKey, JSON.stringify(events));
    } catch (error) {
      setErrorEvents(error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    } finally {
      // Always set loading to false after the fetch attempt.
      setLoadingEvents(false);
    }
  }, [handleLogout, selectedTenantId, userData]); // Dependencies: selectedTenantId and userData

  // Effect to fetch events when selectedTenantId or userData changes,
  // and neither user nor tenant data is loading.
  useEffect(() => {
    if (isAuthenticated() && userData && selectedTenantId && !loadingUser && !loadingTenant) {
      // fetchUserEvents();
      console.log('[EventContext] Fetching user events for tenant:', selectedTenantId);
      console.log(`[EventContext] Selected Tenant ID: ${selectedTenantId} isAuthenticated: ${isAuthenticated()} userData: ${userData ? 'present' : 'null'}`);
      if (userEvents.length === 0) {
        fetchUserEventsV2();
      }
    } else if (!isAuthenticated() || !userData) {
      console.log('[EventContext] Clearing user events due to unauthenticated user or missing user data.');
      console.log(`[EventContext] Selected Tenant ID: ${selectedTenantId} isAuthenticated: ${isAuthenticated()} userData: ${userData ? 'present' : 'null'}`);
      setUserEvents([]); // Clear events if not authenticated or no user data
    }
    // Do not fetch if loadingUser or loadingTenant is true, wait for them to resolve.
  }, [selectedTenantId, userData, loadingUser, loadingTenant, fetchUserEventsV2]);
  // }, [selectedTenantId, userData, loadingUser, loadingTenant, fetchUserEvents, fetchUserEventsV2]); // Added loadingUser, loadingTenant
  

  // const updateSingleEventStatus = useCallback((eventId, newStatus) => {
  //   console.log('Updating event status:', { eventId, newStatus }); // Debug log for input parameters
  //   setUserEvents(prevEvents => {
  //     console.log('Previous events:', prevEvents); // Debug log for previous events
  //     const updatedEvents = prevEvents.map(event =>
  //       event.event_id === eventId
  //         ? { 
  //             ...event, 
  //             event_user_state: { 
  //               ...(event.event_user_state || {}), 
  //               answer: newStatus,
  //               is_participating: newStatus === 'participate',
  //               is_interested: newStatus === 'interested'
  //             } 
  //           }
  //         : event
  //     );
  //     console.log('Updated events:', updatedEvents); // Debug log for updated events
  //     return updatedEvents;
  //   });
  // }, []);

  const updateSingleEventStatus = (eventId, newStatus) => {
    console.log('Updating event status:', { eventId, newStatus }); // Debug log for input parameters
    setUserEvents(prevEvents => {
      console.log('Previous events:', prevEvents); // Debug log for previous events
      const updatedEvents = prevEvents.map(event =>
        event.event_id === eventId
          ? { 
              ...event, 
              event_user_state: { 
                ...(event.event_user_state || {}), 
                answer: newStatus,
                is_participating: newStatus.isParticipating,
                is_interested: newStatus.isInterested
              } 
            }
          : event
      );
      const cacheKey = `my_events_v2_${selectedTenantId}_${userData.email}`;
      localStorage.setItem(cacheKey, JSON.stringify(updatedEvents));
      console.log('Updated events:', updatedEvents); // Debug log for updated events
      return updatedEvents;
    });
  };

  const updateEventRating = (eventId, newRating) => {
    setUserEvents(prevEvents => {
      const updatedEvents = prevEvents.map(event =>
        event.event_id === eventId
          ? { 
              ...event, 
              event_user_state: { 
                ...(event.event_user_state || {}), 
                feedback_value: newRating 
              } 
            }
          : event
      );
      return updatedEvents;
    });
  };

  const value = {
    userEvents,
    loadingEvents,
    errorEvents,
    // fetchUserEvents,
    fetchUserEvent,
    fetchUserEventsV2,
    updateSingleEventStatus,
    updateEventRating,
    // If you need to manually set events from somewhere else, you can expose setUserEvents
    // setUserEvents 
    changeTenantIDCleanup,
  };

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};
